/**
 * CONTRATO de Consulta CM ⇄ API (módulo `negocio`). Versión en CONTRATO_NEGOCIO.
 *
 * ⚠️ Este archivo se COPIA tal cual a los tres repos y debe quedar idéntico
 * (la prueba de cada repo compara su huella con la de los otros dos):
 *   - cdr-sa-consulta-cm/src/shared/negocio.types.ts   (fuente)
 *   - cdr-sa-api/src/modules/negocio/domain/negocio.types.ts
 *   - cdr-sa-admin/src/modules/negocio/negocio.types.ts
 * Sin dependencias, sin `enum` (el admin compila con TS 4.9 y CRA), sólo tipos y
 * constantes literales. Fechas: 'YYYY-MM-DD' (día de México); instantes: ISO-8601.
 *
 * Plan: scratchpad/consulta-cm-20261030/plan.md.
 */

export const CONTRATO_NEGOCIO = '2026-10-30.4' as const; // .2: Responsable con id/tienda/motivo; ResultadoAsignacion. .3: perfiles (fase 3): alcance «tienda», perfil_alcance con regional/supervisor, deFabrica, CodigoPerfil, AsignarPerfilRespuesta. .4: fases 5 y 6: capacidades.metasEnTelefono, GET /negocio/negados, ContratoNegocio

/**
 * Versiones que la app ACEPTA de la API. La app nueva convive con la API v1 de
 * prod (`cc1cd11`, `.3`): allá no vienen `capacidades.metasEnTelefono` ni
 * `/negocio/negados`, y lo que no viene cuenta como apagado. La app nunca
 * rechaza una respuesta por su versión: sólo informa.
 */
export const CONTRATOS_ACEPTADOS = ['2026-10-30.3', '2026-10-30.4'] as const;
export type ContratoNegocio = (typeof CONTRATOS_ACEPTADOS)[number];

// ───────────────────────────── Básicos ─────────────────────────────

export type Cadena = 'TRE' | 'VAZ' | 'TAF' | 'MRH';
export type FechaDia = string; // 'YYYY-MM-DD'
export type Instante = string; // ISO-8601 con zona

export interface TiendaRef {
  cadena: Cadena;
  num: number;
  nombre: string;
  /** De `tienda_responsable` vigente (fase 1); mientras no exista, del catálogo. */
  supervisor: string | null;
  regional: string | null;
}

/** Errores de sesión (401). El cliente los distingue por `codigo`. */
export type CodigoSesion =
  | 'SESION_CERRADA' // corte por cuenta (cerrar sesiones / baja)
  | 'SESION_VENCIDA_ABSOLUTA' // tope de `auth_time` (30 días por omisión)
  | 'REFRESH_REVOCADO' // logout de ESTE equipo
  | 'DISPOSITIVO_REVOCADO'; // ya existe (dispositivos de tienda)

export interface ErrorSesion {
  statusCode: 401;
  codigo: CodigoSesion;
  message: string;
}

// ─────────────────────────── Perfiles ───────────────────────────

/** Módulos. El perfil sólo QUITA; los sensibles nunca vienen implícitos. */
export const MODULOS = [
  'negocio.inicio',
  'negocio.tiendas',
  'negocio.negados',
  'presupuesto',
  'scorecard',
  'metas.ver',
  'metas.capturar',
  'metas.autorizar',
  'finanzas.utilidad', // APAGADO para todos hasta que Finanzas valide (config)
  'sistemas.salud',
] as const;
export type ModuloId = (typeof MODULOS)[number];

export type ClavePerfil =
  | 'direccion'
  | 'regional'
  | 'supervisor'
  | 'compras'
  | 'finanzas'
  | 'operaciones'
  | 'sistemas';

export interface Perfil {
  id: string;
  clave: ClavePerfil | string; // las 7 de fábrica + las que cree TI
  nombre: string;
  descripcion: string | null;
  modulos: ModuloId[];
  activo: boolean;
  /** Uno de los 7 de fábrica (semilla): se edita, no se borra. */
  deFabrica: boolean;
  usuarios: number; // cuántas cuentas lo tienen
  actualizadoEn: Instante;
  actualizadoPor: string | null;
}

export interface CrearPerfilBody {
  clave: string; // [a-z0-9_-]{2,32}, única
  nombre: string;
  descripcion?: string | null;
  modulos: ModuloId[];
}
export type ActualizarPerfilBody = Partial<Omit<CrearPerfilBody, 'clave'>> & { activo?: boolean };

/**
 * Alcance de tiendas en Consulta CM = la UNIÓN de dos fuentes:
 * - `metas_alcance_usuario` (`direccion`, `regional`, `supervisor`), que se asigna
 *   en Metas › Accesos y que Consulta CM sólo LEE;
 * - `perfil_alcance` (`todas`, `cadena`, `regional`, `supervisor`, `tienda`), que
 *   lee `/negocio/*` y, para cuentas CON perfil y SIN alcance de Metas, el Scorecard
 *   (sólo recorta; P45); Metas NUNCA: dar aquí «regional de X» deja ver sus tiendas
 *   en Consulta CM, pero no autoriza nada en Metas.
 * La 118 existe en TAF y en TRE: `tienda` va siempre por cadena Y número.
 */
export type AlcanceNegocio =
  | { tipo: 'todas' }
  | { tipo: 'cadena'; cadena: Cadena }
  | { tipo: 'direccion' }
  | { tipo: 'regional'; nombre: string }
  | { tipo: 'supervisor'; nombre: string }
  | { tipo: 'tienda'; cadena: Cadena; num: number };

/** Lo que se guarda en `perfil_alcance` (`direccion` es sólo de Metas › Accesos). */
export type AlcanceNegocioAsignable = Exclude<AlcanceNegocio, { tipo: 'direccion' }>;

/** PUT /users/:id/perfil — sólo superadmin y admin. Reemplaza el perfil y TODO `perfil_alcance`. */
export interface AsignarPerfilBody {
  perfilId: string | null; // null = quitar perfil
  alcancesNegocio: AlcanceNegocioAsignable[];
}

/**
 * Errores de perfiles (`codigo`). 400: CLAVE_INVALIDA, NOMBRE_INVALIDO, MODULO_DESCONOCIDO,
 * ALCANCE_INVALIDO, NOMBRE_DESCONOCIDO (regional/supervisor que no está en el catálogo),
 * TIENDA_DESCONOCIDA. 404: PERFIL_NO_EXISTE. 409: CLAVE_DUPLICADA, PERFIL_CON_USUARIOS,
 * PERFIL_DE_FABRICA, PERFIL_INACTIVO, ROL_SIN_PERFIL (cuentas compartidas, vendedor, consulta).
 * 403 en una ruta: MODULO_NO_PERMITIDO (el perfil no trae el módulo).
 */
export type CodigoPerfil =
  | 'CLAVE_INVALIDA'
  | 'NOMBRE_INVALIDO'
  | 'MODULO_DESCONOCIDO'
  | 'ALCANCE_INVALIDO'
  | 'NOMBRE_DESCONOCIDO'
  | 'TIENDA_DESCONOCIDA'
  | 'PERFIL_NO_EXISTE'
  | 'CLAVE_DUPLICADA'
  | 'PERFIL_CON_USUARIOS'
  | 'PERFIL_DE_FABRICA'
  | 'PERFIL_INACTIVO'
  | 'ROL_SIN_PERFIL'
  | 'MODULO_NO_PERMITIDO';

// ─────────────────────────── /me/perfil ───────────────────────────

export interface Capacidades {
  /** La API lo ofrece (backend presente y encendido). Nunca constante compilada. */
  presupuesto: boolean;
  scorecard: boolean;
  negados: boolean;
  /** `finanzas.utilidad` en el perfil Y `config.negocio.utilidad.habilitada`. */
  utilidad: boolean;
  /** Primer día con comparativo contra el año anterior (null = aún sin relleno). */
  comparativoDesde: FechaDia | null;
  /**
   * `.4` (fase 6): autorizar Metas desde el teléfono. `metas.autorizar` en el perfil
   * Y `config.negocio.metas.autorizarEnTelefono` en true (por entorno: encendida en
   * staging, ausente en prod). Ausente (API `.3`) = false: Pendientes de sólo lectura.
   */
  metasEnTelefono?: boolean;
}

export type MotivoNoEntra = 'ROL_NO_PERMITIDO' | 'SIN_PERFIL' | 'SIN_ALCANCE' | 'SIN_MODULOS';

/** GET /me/perfil — cualquier cuenta autenticada. */
export interface MePerfil {
  contrato: ContratoNegocio;
  usuario: { id: string; nombre: string; email: string; rol: string };
  perfil: Pick<Perfil, 'id' | 'clave' | 'nombre'> | null;
  /**
   * true = superadmin SIN perfil asignado: entra como «sistemas» (cuenta de rescate). Nunca
   * trae `finanzas.utilidad`.
   */
  perfilImplicito: boolean;
  modulos: ModuloId[];
  alcances: AlcanceNegocio[];
  /** Tiendas visibles en Consulta CM (ya recortadas por alcance). */
  tiendas: TiendaRef[];
  capacidades: Capacidades;
  puedeEntrarNegocio: boolean;
  motivoNoEntra: MotivoNoEntra | null;
  sesion: { authTime: Instante | null; venceAbsoluto: Instante | null };
}

// ─────────────────────────── Periodos y valores ───────────────────────────

export type PeriodoTipo = 'dia' | 'hoy' | 'semana' | 'mes' | 'rango';
/** `aa` = año anterior según `config.negocio.comparativo` (omisión: 364 días). */
export type Comparar = 'aa' | 'ninguno';
export type Agrupar = 'tienda' | 'supervisor' | 'regional' | 'cadena';

export interface PeriodoResuelto {
  tipo: PeriodoTipo;
  desde: FechaDia;
  hasta: FechaDia; // inclusive
  /** «Jue. 29 de oct. de 2026», «Semana 44 · 26 oct. – 1 nov.» */
  etiqueta: string;
  /** Hoy o mes en curso: hasta qué hora se compara (0-23, hora de México). */
  hastaHora: number | null;
  comparadoCon: { desde: FechaDia; hasta: FechaDia; etiqueta: string } | null;
  /** Para las flechas ‹ ›: null = no hay más. */
  anterior: { fecha: FechaDia } | null;
  siguiente: { fecha: FechaDia } | null;
}

export type SinComparativo = 'SIN_DATO_AA' | 'TIENDA_NUEVA' | 'NO_APLICA';

export interface Valor {
  v: number | null; // null = sin dato (nunca 0 inventado)
  aa: number | null;
  /** (v − aa) / aa × 100, redondeado a 2. null si `sc`. */
  varPct: number | null;
  sc: SinComparativo | null;
}

export const COLUMNAS = [
  'venta', // neta sin IVA
  'tickets', // con préstamos, como el reporte semanal
  'pares', // calzado (IFAMD = 'D001')
  'piezas', // todos los artículos (SUM AICANTF); null hasta materializar
  'paresPorTicket', // sobre tickets SIN préstamos
  'ticketPromedio', // sobre tickets SIN préstamos
  'pctMeta', // SISCOM; nunca con comparativo
  'descuento',
  'contado',
  'credito',
  'utilidad', // sólo si capacidades.utilidad
  'margen', // sólo si capacidades.utilidad
] as const;
export type ColumnaId = (typeof COLUMNAS)[number];

export interface Actualizado {
  /** Último día cerrado en el snapshot. */
  cerradoHasta: FechaDia | null;
  /** Hora de la copia de hoy (cada 30 min); null si el periodo no incluye hoy. */
  hoyActualizadoA: Instante | null;
}

// ─────────────────────────── /negocio/tiendas ───────────────────────────

/** GET /negocio/tiendas — query. */
export interface ConsultaTiendas {
  periodo: PeriodoTipo;
  fecha?: FechaDia; // ancla (dia/semana/mes); omisión: ayer
  desde?: FechaDia; // rango (≤ 366 días)
  hasta?: FechaDia;
  comparar?: Comparar; // omisión 'aa'
  agrupar?: Agrupar; // omisión 'tienda'
  cadena?: Cadena[];
  regional?: string[];
  supervisor?: string[];
  bajoMeta?: boolean;
  bajoAA?: boolean;
}

export interface FilaTiendas {
  /** Agrupar=tienda → la tienda; si no, el grupo. */
  clave: string; // 'VAZ#132' | 'sup:<nombre>' | 'reg:<nombre>' | 'cad:VAZ'
  etiqueta: string;
  tienda: TiendaRef | null;
  tiendas: number; // cuántas tiendas suma
  celdas: Partial<Record<ColumnaId, Valor>>;
}

export interface RespuestaTiendas {
  contrato: ContratoNegocio;
  periodo: PeriodoResuelto;
  agrupar: Agrupar;
  /** Columnas permitidas a ESTA cuenta, en orden. */
  columnas: ColumnaId[];
  total: FilaTiendas;
  filas: FilaTiendas[];
  /** Filtros que puede ofrecer (ya recortados por alcance). */
  opciones: { cadenas: Cadena[]; regionales: string[]; supervisores: string[] };
  actualizado: Actualizado;
  avisos: string[]; // «Comparativo 2025 pendiente de relleno», etc.
}

// ─────────────────────────── /negocio/inicio ───────────────────────────

export interface Kpi {
  id: 'venta' | 'pctMetaSemana' | 'ticketPromedio' | 'paresPorTicket' | 'tasaAtencion' | 'tickets';
  titulo: string;
  valor: Valor;
  formato: 'moneda' | 'porcentaje' | 'decimal' | 'entero';
  nota: string | null; // «s/c año ant. (SISCOM sin 2025)»
}

export interface ModuloInicio {
  id: ModuloId;
  titulo: string;
  descripcion: string;
  ruta: string; // ruta de la app
}

/** GET /negocio/inicio?fecha= (omisión: ayer). */
export interface RespuestaInicio {
  contrato: ContratoNegocio;
  perfil: ClavePerfil | string | null;
  titulo: string; // «Tus tiendas, ayer» / «La cadena, ayer»
  periodo: PeriodoResuelto;
  kpis: Kpi[];
  tiendasEnRojo: Array<{ tienda: TiendaRef; varPct: number }>;
  /** null = el perfil no tiene el módulo de Metas. */
  pendientes: { metasPorAutorizar: number | null; capturasPorEnviar: number | null } | null;
  modulos: ModuloInicio[];
  actualizado: Actualizado;
  avisos: string[];
}

// ─────────────────────────── /negocio/negados (.4) ───────────────────────────

/**
 * Negados para Compras (fase 5). Sólo las vistas del tablero (120 días); sin ERP
 * y SIN existencia (la de la talla negada llega en el correo semanal de Compras).
 * La ruta existe sólo si la API la ofrece (`capacidades.negados`).
 */
export type AgruparNegados = 'modelo' | 'talla' | 'tienda';
/** `anterior` = el mismo día de la semana anterior, la semana, el mes o el rango previo. */
export type CompararNegados = 'anterior' | 'ninguno';

/** GET /negocio/negados — query. La API rechaza (400) cualquier campo de más. */
export interface ConsultaNegados {
  periodo: PeriodoTipo;
  fecha?: FechaDia; // ancla (dia/semana/mes); omisión: ayer
  desde?: FechaDia; // rango
  hasta?: FechaDia;
  comparar?: CompararNegados; // omisión 'anterior'
  agrupar?: AgruparNegados; // omisión 'modelo'
  cadena?: Cadena[];
  regional?: string[];
  supervisor?: string[];
  /** Estatus AL CONSULTAR (el del tablero), no el de hoy. */
  estatus?: string[];
  /** 'VAZ#132', el formato de `FilaTiendas.clave`. */
  tienda?: string;
  /** Modelo-color de 10 ('8320202NEG'). */
  modelo?: string;
  top?: number; // 1-200, omisión 40
}

export interface ModeloNegado {
  modeloColor: string; // '6640028NEG'
  modelo: string; // '6640028' (la foto)
  descripcion: string;
  /** El del último negado del periodo. */
  estatus: string;
  estatusDistintos: number;
  cadenas: Cadena[];
  imagenUrl: string | null;
}

export interface FilaNegados {
  clave: string; // 'mod:8320202NEG' | 'tal:8320202NEG:250' | 'TRE#12'
  etiqueta: string;
  modelo: ModeloNegado | null;
  /** Tal cual el tablero y el Excel de Compras: '250'. */
  talla: string | null;
  tienda: TiendaRef | null;
  /** Tienda que no está en el catálogo (p. ej. la VAZ 250): `tienda` null. */
  fueraDeCatalogo: boolean;
  negados: number;
  anterior: number | null;
  varPct: number | null;
  tiendas: number;
  modelos: number;
  /** Sólo agrupar=modelo: hasta 12, de más a menos negados. */
  tallas: Array<{ talla: string; negados: number }> | null;
  /** Sólo agrupar=tienda y sin `modelo`. */
  consultas: number | null;
  tasa: number | null;
}

export interface TotalNegados {
  /** null = sin dato (el periodo cae antes de los 120 días): nunca un 0 inventado. */
  negados: number | null;
  anterior: number | null;
  varPct: number | null;
  modelos: number | null;
  tallas: number | null;
  tiendas: number | null;
  consultas: number | null;
  tasa: number | null;
}

export interface RespuestaNegados {
  contrato: ContratoNegocio;
  /** `comparadoCon` = el periodo ANTERIOR (no el año anterior). */
  periodo: PeriodoResuelto;
  agrupar: AgruparNegados;
  top: number;
  total: TotalNegados;
  porEstatus: Array<{ estatus: string; negados: number; anterior: number | null }>;
  filas: FilaNegados[];
  restantes: { filas: number; negados: number };
  opciones: { cadenas: Cadena[]; regionales: string[]; supervisores: string[]; estatus: string[] };
  /** La vista guarda 120 días. null = vista vacía. */
  cobertura: { desde: FechaDia | null; hasta: FechaDia | null };
  /** Hora (0-23, México) del último negado de hoy; null si el periodo no incluye hoy. */
  actualizado: { hastaHora: number | null };
  avisos: string[];
}

// ─────────────────────────── Sesiones y rotación ───────────────────────────

/** POST /auth/logout — cierra SÓLO este equipo (jti del refresh). 204. */
export interface LogoutBody {
  refreshToken: string;
}

/** POST /users/:id/cerrar-sesiones — superadmin y admin. */
export interface CerrarSesionesBody {
  motivo: string; // 3-200
  /** Obligatorio en cuentas compartidas (branch_office, warehouse_staff). */
  confirmarCuentaCompartida?: boolean;
}
export interface CerrarSesionesRespuesta {
  userId: string;
  sesionesValidasDesde: Instante;
  socketsCerrados: number;
  cuentaCompartida: boolean;
}

/** POST /users/:id/baja — superadmin y admin. Un solo paso, idempotente. */
export interface BajaBody {
  motivo: string;
  confirmarCuentaCompartida?: boolean;
}
export interface BajaRespuesta {
  userId: string;
  desactivada: true;
  sesionesValidasDesde: Instante;
  socketsCerrados: number;
  alcancesMetasRevocados: number;
  alcancesNegocioRevocados: number;
  perfilRetirado: boolean;
  /** Si era supervisor o regional: tiendas que quedan sin responsable. */
  tiendasSinResponsable: TiendaRef[];
}

/** Campos nuevos en GET /users (listado del admin). */
export interface UsuarioSesionInfo {
  ultimoAcceso: Instante | null;
  sesionesValidasDesde: Instante | null;
  sesionesCerradasPor: string | null;
  sesionesCerradasMotivo: string | null;
  perfil: Pick<Perfil, 'id' | 'clave' | 'nombre'> | null;
  /** Sólo `perfil_alcance` (los de Metas se ven en Metas › Accesos). */
  alcancesNegocio: AlcanceNegocioAsignable[];
}

/** Respuesta de PUT /users/:id/perfil. */
export interface AsignarPerfilRespuesta extends UsuarioSesionInfo {
  userId: string;
  /** p. ej. «regional de X: en Metas no autoriza nada (eso se da en Metas › Accesos)». */
  avisos: string[];
}

// ─────────────────────────── Responsables por tienda ───────────────────────────

export interface Responsable {
  id: number | null; // null con origen 'catalogo'
  cadena: Cadena;
  num: number;
  tienda: string;
  supervisor: string | null;
  regional: string | null;
  vigenteDesde: FechaDia; // '2000-01-01' = la semilla del catálogo compilado
  vigenteHasta: FechaDia | null; // null = abierta; < vigenteDesde = anulada (se corrigió el mismo día)
  asignadoPor: string;
  asignadoEn: Instante | null; // null con origen 'catalogo'
  motivo: string | null;
  origen: 'tabla' | 'catalogo'; // catálogo = aún sin fila propia
}

/** GET /tiendas/responsables?fecha= (omisión: hoy) → Responsable[] (una por tienda). */
/** GET /tiendas/:cadena/:num/responsables → historial (Responsable[]). */
/** PUT /tiendas/:cadena/:num/responsable — superadmin y admin. */
export interface AsignarResponsableBody {
  supervisor: string | null;
  regional: string | null;
  vigenteDesde: FechaDia; // de hoy − 7 a hoy + 90; cierra la vigente el día anterior
  motivo?: string; // ≤ 200
}

/**
 * Respuesta del PUT. 400: FECHA_INVALIDA, FECHA_FUERA_DE_RANGO, NOMBRE_LARGO (> 80),
 * MOTIVO_LARGO. 404: la tienda no está en el catálogo. 409: VIGENCIA_ANTERIOR (antes de la
 * vigente), ASIGNACION_SIMULTANEA. Igual a la vigente → 200 con sinCambio y sin escribir.
 * ⚠️ Los nombres son llaves de acceso de Metas: reasignar mueve quién ve, captura y autoriza.
 */
export interface ResultadoAsignacion {
  responsable: Responsable;
  sinCambio: boolean;
  avisos: string[]; // nombre nuevo sin alcance, tienda sin supervisor/regional, alguien sin tiendas, fecha futura
}
